import { useParams, Link } from 'react-router-dom';
import { categories, comics } from '@/mocks/comics';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryComics = comics.filter((c) => c.category === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-foreground-500 text-lg">分类未找到</p>
          <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${category.name}韩漫免费阅读 - ${category.description} - 51漫画网`}
        description={`51漫画网${category.name}分类，${category.description}。海量${category.name}韩漫免费在线阅读，下拉式漫画全集呈现完整剧情，每日更新热门${category.name}韩国漫画。`}
        keywords={`${category.name}韩漫,${category.name}漫画,${category.name}韩国漫画,51漫画网,免费${category.name}漫画`}
        canonicalUrl={`/category/${slug}`}
      />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category.name },
          ]}
        />
        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {category.name}韩漫
              </h1>
              <p className="text-sm text-foreground-500 mt-2">{category.description}</p>
            </div>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
              <Link
                to="/"
                onClick={() => window.scrollTo(0, 0)}
                className="px-4 py-2 text-sm font-medium rounded-full bg-background-200 text-foreground-600 hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => window.scrollTo(0, 0)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200 text-foreground-600 hover:bg-background-300'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryComics.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无漫画</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                {categoryComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.id}`}
                    onClick={() => window.scrollTo(0, 0)}
                    className="group cursor-pointer"
                    data-product-shop
                  >
                    <div className="relative aspect-[5/7] rounded-lg overflow-hidden mb-3 bg-background-200">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2 px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-md">
                        {comic.rating}分
                      </div>
                      {comic.status === '已完结' && (
                        <div className="absolute top-2 right-2 px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded-md">
                          已完结
                        </div>
                      )}
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">
                      {comic.author} · {comic.chapters}话
                    </p>
                    <p className="text-xs text-foreground-400 mt-0.5">{comic.views}次阅读</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}